import type { ReactNode } from "react";
import {
  IconChevronLeft,
  IconChevronRight,
  IconCog,
  IconGrid,
  IconInfo,
  IconList,
  IconPlus,
  IconSearch,
  IconStar,
} from "../layout/icons.tsx";

export type BreadcrumbItem = { label: string };

export type StandardTablePageProps = {
  title: string;
  pageInfoTitle?: string;
  breadcrumbs?: BreadcrumbItem[];
  onPageSettings?: () => void;
  searchPlaceholder?: string;
  searchValue: string;
  onSearchChange: (value: string) => void;
  toolbarAfterView?: ReactNode;
  showViewToggle?: boolean;
  viewMode?: "list" | "grid";
  onViewModeChange?: (mode: "list" | "grid") => void;
  onToggleFavorite?: () => void;
  favoriteActive?: boolean;
  primaryCtaLabel?: string;
  onPrimaryCta?: () => void;
  primaryCtaDisabled?: boolean;
  showPrimaryCta?: boolean;
  errorMessage?: string | null;
  onRetry?: () => void;
  totalItems: number;
  page: number;
  pageSize: number;
  pageSizeOptions?: number[];
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  fillHeight?: boolean;
  children: ReactNode;
};

const defaultPageSizes = [10, 25, 50];

export function StandardTablePage({
  title,
  pageInfoTitle,
  breadcrumbs,
  onPageSettings,
  searchPlaceholder = "Search…",
  searchValue,
  onSearchChange,
  toolbarAfterView,
  showViewToggle = true,
  viewMode = "list",
  onViewModeChange,
  onToggleFavorite,
  favoriteActive = false,
  primaryCtaLabel = "Add",
  onPrimaryCta,
  primaryCtaDisabled,
  showPrimaryCta = true,
  errorMessage,
  onRetry,
  totalItems,
  page,
  pageSize,
  pageSizeOptions = defaultPageSizes,
  onPageChange,
  onPageSizeChange,
  fillHeight = false,
  children,
}: StandardTablePageProps) {
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const safePage = Math.min(page, totalPages);
  const start = totalItems === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const end = Math.min(totalItems, safePage * pageSize);

  return (
    <section
      className={fillHeight ? "ds-tp ds-tp--fill" : "ds-tp"}
      aria-label={title}
    >
      <header className="ds-tp__header">
        <div className="ds-tp__header-left">
          {breadcrumbs?.length ? (
            <nav
              className="ds-text-secondary"
              aria-label="Breadcrumb"
              style={{ fontSize: 12 }}
            >
              <ol
                style={{
                  display: "flex",
                  gap: 8,
                  listStyle: "none",
                  margin: 0,
                  padding: 0,
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                {breadcrumbs.map((b, i) => (
                  <li
                    key={`${b.label}-${i}`}
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    {i > 0 ? <span aria-hidden>/</span> : null}
                    <span>{b.label}</span>
                  </li>
                ))}
              </ol>
            </nav>
          ) : null}
          <div className="ds-tp__title-wrap">
            <h1 className="ds-tp__title">{title}</h1>
            {pageInfoTitle ? (
              <button
                type="button"
                className="ds-tp__title-info"
                aria-label="Page information"
                title={pageInfoTitle}
              >
                <IconInfo title={undefined} />
              </button>
            ) : null}
          </div>
        </div>
        {onPageSettings ? (
          <div className="ds-tp__header-actions">
            <button
              type="button"
              className="ds-tp__icon-btn"
              aria-label="Page settings"
              onClick={() => onPageSettings()}
            >
              <IconCog title="Settings" />
            </button>
          </div>
        ) : null}
      </header>

      <div className="ds-tp__toolbar" role="toolbar" aria-label="Table actions">
        <div className="ds-tp__toolbar-left">
          <div className="ds-tp__search-wrap">
            <label htmlFor="ds-tp-search" className="visually-hidden">
              Search
            </label>
            <IconSearch title={undefined} />
            <input
              id="ds-tp-search"
              className="ds-tp__search"
              type="text"
              role="searchbox"
              placeholder={searchPlaceholder}
              value={searchValue}
              onChange={(e) => onSearchChange(e.target.value)}
              autoComplete="off"
              enterKeyHint="search"
            />
          </div>
          {onToggleFavorite ? (
            <button
              type="button"
              className="ds-tp__icon-btn ds-tp__toolbar-icon"
              aria-label="Toggle favorites"
              aria-pressed={favoriteActive}
              onClick={onToggleFavorite}
              style={
                favoriteActive
                  ? { color: "var(--color-accent, #2932E5)" }
                  : undefined
              }
            >
              <IconStar title={undefined} />
            </button>
          ) : null}
          {showViewToggle && onViewModeChange ? (
            <>
              <button
                type="button"
                className="ds-tp__icon-btn ds-tp__toolbar-icon"
                aria-label="List view"
                aria-pressed={viewMode === "list"}
                onClick={() => onViewModeChange("list")}
              >
                <IconList title={undefined} />
              </button>
              <button
                type="button"
                className="ds-tp__icon-btn ds-tp__toolbar-icon"
                aria-label="Grid view"
                aria-pressed={viewMode === "grid"}
                onClick={() => onViewModeChange("grid")}
              >
                <IconGrid title={undefined} />
              </button>
            </>
          ) : null}
          {toolbarAfterView ? (
            <div className="ds-tp__toolbar-slot">{toolbarAfterView}</div>
          ) : null}
        </div>
        <div className="ds-tp__toolbar-right">
          {showPrimaryCta && onPrimaryCta ? (
            <button
              type="button"
              className="ds-tp__cta-primary"
              onClick={onPrimaryCta}
              disabled={primaryCtaDisabled}
            >
              <IconPlus title={undefined} />
              {primaryCtaLabel}
            </button>
          ) : null}
        </div>
      </div>

      <div className="ds-tp__table-wrap">
        {errorMessage ? (
          <div className="ds-tp__error-banner" role="alert">
            <span>{errorMessage}</span>
            {onRetry ? (
              <button type="button" onClick={onRetry}>
                Retry
              </button>
            ) : null}
          </div>
        ) : null}
        {children}
      </div>

      <footer className="ds-tp__footer" aria-label="Pagination">
        <span>
          {totalItems === 0
            ? "0 items"
            : `${start}-${end} of ${totalItems} ${totalItems === 1 ? "item" : "items"}`}
        </span>
        <div className="ds-tp__footer-right">
          <label
            className="ds-text-secondary"
            style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
          >
            Rows per page
            <select
              className="ds-tp__page-select"
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              aria-label="Rows per page"
            >
              {pageSizeOptions.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
          <div
            className="ds-tp__pager"
            role="navigation"
            aria-label="Page navigation"
          >
            <button
              type="button"
              aria-label="Previous page"
              disabled={safePage <= 1}
              onClick={() => onPageChange(safePage - 1)}
            >
              <IconChevronLeft title={undefined} />
            </button>
            <span className="ds-text-secondary" aria-current="page">
              Page {safePage} / {totalPages}
            </span>
            <button
              type="button"
              aria-label="Next page"
              disabled={safePage >= totalPages}
              onClick={() => onPageChange(safePage + 1)}
            >
              <IconChevronRight title={undefined} />
            </button>
          </div>
        </div>
      </footer>
    </section>
  );
}
